import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, MapPin, Loader2, X } from '@/ui/icons'
import { Input } from '@/ui/components'
import { Button } from '@/ui/components'
import { RoutesApi } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'

/**
 * Format category string to be more user-friendly
 */
function formatCategory(category: string | string[] | undefined | null): string {
  if (!category) return ''
  
  // Handle array of categories (take first one)
  let categoryStr: string
  if (Array.isArray(category)) {
    if (category.length === 0) return ''
    categoryStr = category[0]
  } else if (typeof category === 'string') {
    categoryStr = category
  } else {
    // If it's an object or other type, try to convert to string
    categoryStr = String(category)
  }
  
  // Handle common category formats
  const categoryLower = categoryStr.toLowerCase()
  
  // Map common Mapbox categories to readable names
  const categoryMap: Record<string, string> = {
    'gas_station': 'Gas Station',
    'gas station': 'Gas Station',
    'fuel': 'Gas Station',
    'grocery': 'Grocery Store',
    'grocery_store': 'Grocery Store',
    'supermarket': 'Supermarket',
    'restaurant': 'Restaurant',
    'cafe': 'Cafe',
    'coffee': 'Coffee Shop',
    'parking': 'Parking',
    'parking_lot': 'Parking Lot',
    'retail': 'Retail Store',
    'shopping': 'Shopping',
    'store': 'Store',
    'pharmacy': 'Pharmacy',
    'bank': 'Bank',
    'atm': 'ATM',
    'hotel': 'Hotel',
    'lodging': 'Lodging',
    'campground': 'Campground',
    'rv_park': 'RV Park',
    'attraction': 'Attraction',
    'viewpoint': 'Viewpoint',
    'scenic_overlook': 'Scenic Overlook'
  }
  
  // Check for exact match first
  if (categoryMap[categoryLower]) {
    return categoryMap[categoryLower]
  }
  
  // Check for partial matches
  for (const [key, value] of Object.entries(categoryMap)) {
    if (categoryLower.includes(key) || key.includes(categoryLower)) {
      return value
    }
  }
  
  // Format as title case if no match
  return categoryStr
    .split(/[_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export interface LocationSearchResult {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
  category?: string | string[]
  mapbox_id?: string
}

interface LocationSearchInputProps {
  value: string
  onChange: (location: string) => void
  onLocationSelect?: (result: LocationSearchResult) => void
  placeholder?: string
  className?: string
  proximity?: { latitude: number; longitude: number }
}

export function LocationSearchInput({
  value,
  onChange,
  onLocationSelect,
  placeholder = 'Search for a location...',
  className = '',
  proximity
}: LocationSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState(value)
  const [results, setResults] = useState<LocationSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { getAccessToken } = useAuth()
  const apiRef = useRef<RoutesApi | null>(null)
  const sessionTokenRef = useRef<string | null>(null)

  // Initialize API client and session token
  useEffect(() => {
    if (!apiRef.current) {
      apiRef.current = new RoutesApi(getAccessToken)
    }
    
    // Generate or retrieve session token from localStorage (persists across page reloads)
    if (!sessionTokenRef.current) {
      const storedToken = localStorage.getItem('mapbox_search_session_token')
      if (storedToken) {
        sessionTokenRef.current = storedToken
      } else {
        // Generate a new UUID for session token
        const newToken = crypto.randomUUID()
        sessionTokenRef.current = newToken
        localStorage.setItem('mapbox_search_session_token', newToken)
      }
    }
  }, [getAccessToken])

  // Get user's location on component mount (for proximity bias)
  useEffect(() => {
    // Use provided proximity if available, otherwise try to get user's location
    if (proximity) {
      setUserLocation(proximity)
      return
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      // Try to get cached location from localStorage first
      const cachedLocation = localStorage.getItem('user_location')
      if (cachedLocation) {
        try {
          const { latitude, longitude, timestamp } = JSON.parse(cachedLocation)
          // Use cached location if it's less than 1 hour old
          if (Date.now() - timestamp < 3600000) {
            setUserLocation({ latitude, longitude })
            return
          }
        } catch (e) {
          // Invalid cache, clear it
          localStorage.removeItem('user_location')
        }
      }

      // Request current location (with timeout and error handling)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          setUserLocation(location)
          // Cache the location for 1 hour
          localStorage.setItem('user_location', JSON.stringify({
            ...location,
            timestamp: Date.now()
          }))
        },
        (error) => {
          // Silently fail - location is optional for search
        },
        {
          timeout: 5000,
          enableHighAccuracy: false,
          maximumAge: 3600000 // Accept cached location up to 1 hour old
        }
      )
    }
  }, [proximity])

  // Sync external value with internal state
  useEffect(() => {
    setSearchQuery(value)
  }, [value])

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setResults([])
      setIsSearching(false)
      return
    }

    if (!apiRef.current || !sessionTokenRef.current) return

    setIsSearching(true)
    try {
      // Use user location if available (either from props or geolocation)
      const searchProximity = proximity || userLocation
      
      // Only fetch suggestions (not full details) to save API calls
      // Full details will be fetched when user selects a result
      const searchResults = await apiRef.current.forwardGeocode(
        query,
        searchProximity?.latitude,
        searchProximity?.longitude,
        5,
        sessionTokenRef.current,
        false // Don't fetch full details yet
      )
      setResults(searchResults)
      setShowResults(true)
      setSelectedIndex(-1)
    } catch (error) {
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }, [proximity, userLocation])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    onChange(newValue)

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Debounce search (increased from 300ms to 600ms to reduce API calls)
    if (newValue.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(newValue)
      }, 600)
    } else {
      setResults([])
      setShowResults(false)
    }
  }

  const handleSelect = async (result: LocationSearchResult) => {
    // If result doesn't have coordinates (suggestion only), fetch full details
    if ((result.latitude === 0 && result.longitude === 0) && result.mapbox_id && sessionTokenRef.current && apiRef.current) {
      try {
        setIsSearching(true)
        const searchProximity = proximity || userLocation
        const fullDetails = await apiRef.current.retrieveSuggestionDetails(
          result.mapbox_id,
          sessionTokenRef.current,
          searchProximity?.latitude,
          searchProximity?.longitude
        )
        
        // Update result with full details
        const updatedResult: LocationSearchResult = {
          ...result,
          latitude: fullDetails.latitude,
          longitude: fullDetails.longitude,
          address: fullDetails.address || result.address,
          name: fullDetails.name || result.name
        }
        
        setSearchQuery(updatedResult.name)
        onChange(updatedResult.name)
        setShowResults(false)
        onLocationSelect?.(updatedResult)
      } catch (error) {
        // Fallback to original result even if retrieval fails
        setSearchQuery(result.name)
        onChange(result.name)
        setShowResults(false)
        onLocationSelect?.(result)
      } finally {
        setIsSearching(false)
      }
    } else {
      // Result already has coordinates, use as-is
      setSearchQuery(result.name)
      onChange(result.name)
      setShowResults(false)
      onLocationSelect?.(result)
    }
  }

  const handleClear = () => {
    setSearchQuery('')
    onChange('')
    setResults([])
    setShowResults(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => 
        prev < results.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => prev > -1 ? prev - 1 : -1)
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    } else if (e.key === 'Escape') {
      setShowResults(false)
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true)
            }
          }}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {searchQuery && !isSearching && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {results.map((result, index) => (
            <button
              key={result.id}
              type="button"
              onClick={() => handleSelect(result)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-adventure-50' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-adventure-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">
                    {result.name}
                  </div>
                  {result.category && (
                    <div className="text-xs text-adventure-600 font-medium truncate mt-0.5">
                      {formatCategory(result.category)}
                    </div>
                  )}
                  {result.address && result.address !== result.name && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {result.address}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

